import { Star, ExternalLink, ShieldCheck } from "lucide-react";
import { BUSINESS } from "@/lib/config";

const certifications = [
  "Licensed & Insured",
  "C-DET Trained",
  "EPA Compliant",
];

const highlights = [
  {
    title: "Real customers, real reviews",
    text: "Every star comes from a verified St. Augustine homeowner who hired BID directly. We don't buy reviews — we earn them.",
  },
  {
    title: "Top-rated in St. Johns County",
    text: "Consistently 5-star service across the Greater St. Augustine area, from Anastasia Island to World Golf Village.",
  },
  {
    title: "Backed by Google",
    text: "Read every word — good or bad — straight on our Google Business profile. Nothing edited, nothing hidden.",
  },
];

export default function Testimonials() {
  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="bg-slate-900 text-white border-b border-slate-900"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-6">
            <div className="flex items-center gap-3 mb-5">
              <span className="bid-divider" />
              <span className="text-xs tracking-[0.25em] uppercase font-bold text-[#FF2A2A]">
                Trusted in St. Augustine
              </span>
            </div>
            <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight leading-tight">
              {BUSINESS.googleRating} stars on Google.
              <br />
              And we&apos;re just getting started.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 flex items-end">
            <div className="flex flex-wrap gap-x-6 gap-y-3">
              {certifications.map((c) => (
                <div
                  key={c}
                  className="text-xs tracking-widest uppercase font-bold text-slate-300 border border-slate-700 px-3 py-2"
                >
                  {c}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Real Google reviews CTA card */}
          <a
            href={BUSINESS.googleMapsUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="google-reviews-cta"
            className="lg:col-span-5 group bg-white text-slate-900 p-10 flex flex-col justify-between hover:-translate-y-1 transition-transform"
          >
            <div>
              <div className="flex items-center gap-2 mb-4">
                <svg viewBox="0 0 48 48" className="w-8 h-8" aria-hidden>
                  <path
                    fill="#FFC107"
                    d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"
                  />
                  <path
                    fill="#FF3D00"
                    d="M6.3 14.7l6.6 4.8C14.6 16 18.9 13 24 13c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
                  />
                  <path
                    fill="#4CAF50"
                    d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35 26.7 36 24 36c-5.3 0-9.7-3.4-11.3-8.1l-6.5 5C9.5 39.6 16.2 44 24 44z"
                  />
                  <path
                    fill="#1976D2"
                    d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6.2 5.2c-.4.4 6.6-4.8 6.6-14.7 0-1.3-.1-2.4-.4-3.5z"
                  />
                </svg>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">
                  Google Business Profile
                </div>
              </div>
              <div className="flex items-baseline gap-3 mb-2">
                <div
                  data-testid="google-rating"
                  className="font-display text-7xl font-black tracking-tighter"
                >
                  {BUSINESS.googleRating}
                </div>
                <div className="flex items-center gap-0.5 mb-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      size={20}
                      fill="#FFC107"
                      stroke="#FFC107"
                    />
                  ))}
                </div>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Read every verified review for{" "}
                <span className="font-bold text-slate-900">
                  {BUSINESS.name}
                </span>{" "}
                directly on Google — what real customers say, no edits.
              </p>
            </div>

            <div className="mt-8 flex items-center gap-2 font-bold uppercase tracking-widest text-sm group-hover:text-[#002FA7] transition-colors">
              See all reviews on Google
              <ExternalLink size={16} />
            </div>
          </a>

          {/* Highlights */}
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-6">
            {highlights.map((h, i) => (
              <div
                key={h.title}
                data-testid={`trust-highlight-${i}`}
                className="bg-slate-800 border border-slate-700 p-6 flex flex-col"
              >
                <ShieldCheck
                  size={24}
                  className="text-[#FF2A2A] mb-4"
                  strokeWidth={2}
                />
                <div className="font-display font-bold text-lg uppercase tracking-tight mb-2 leading-tight">
                  {h.title}
                </div>
                <p className="text-sm text-slate-300 leading-relaxed">
                  {h.text}
                </p>
              </div>
            ))}

            <a
              href={BUSINESS.googleMapsUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="leave-review-cta"
              className="sm:col-span-3 bg-[#FF2A2A] hover:bg-[#cc2222] transition-colors p-6 flex items-center justify-between gap-4 group"
            >
              <div>
                <div className="font-display font-black text-2xl uppercase tracking-tight">
                  Been our customer?
                </div>
                <div className="text-sm text-white/90 mt-1">
                  Leave us a Google review — it takes 30 seconds.
                </div>
              </div>
              <div className="font-bold uppercase tracking-widest text-sm flex items-center gap-2 shrink-0 border-2 border-white px-5 py-3">
                Write a review
                <ExternalLink size={16} />
              </div>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
